import type { OutboxMessage } from './types';

/**
 * Durable storage for the Outbox.
 *
 * Deliberately the same shape as `api/deviceKey.ts`'s `DeviceKeyStorage`: a
 * tiny interface, a hand-rolled IndexedDB adapter, and an in-memory double that
 * satisfies the same contract. There is no IndexedDB wrapper library in
 * `package.json` and this is not enough code to justify adding one.
 *
 * ## The one primitive that matters: {@link OutboxStorage.mutate}
 *
 * Read-modify-write inside a **single** `readwrite` transaction. IndexedDB
 * serialises overlapping `readwrite` transactions on the same store across
 * every tab of the origin, so this is a real compare-and-set — which is what
 * makes "two tabs must not both flush the same message" true rather than
 * probable. Leader election (`realtime/leader.ts`) reduces the contention; it
 * does not remove it, because that election explicitly accepts two leaders for
 * a round ("the cost of two leaders for one round is one duplicate poll"). A
 * duplicate poll is free. A duplicate send is not.
 *
 * The callback must be **synchronous**: an IndexedDB transaction closes as soon
 * as control returns to the event loop with no pending request, so an `await`
 * inside one aborts it. The type says so and every caller obeys it.
 */
export interface OutboxStorage {
  all(): Promise<OutboxMessage[]>;
  get(id: string): Promise<OutboxMessage | undefined>;
  /** Insert. Rejects if the id is taken, so an id collision is never a silent overwrite. */
  add(record: OutboxMessage): Promise<void>;
  /**
   * Atomic read-modify-write. The callback returns the record to store, or
   * `undefined` to leave the store untouched. Resolves with what was stored.
   */
  mutate(
    id: string,
    change: (current: OutboxMessage) => OutboxMessage | undefined,
  ): Promise<OutboxMessage | undefined>;
  remove(id: string): Promise<void>;
  clear(): Promise<void>;
  /**
   * Delete the whole database, and say whether it is **confirmed** gone.
   *
   * Sign-out's step (ADR 0058, ADR 0060 §3): the Outbox holds whole outgoing
   * messages the server has never seen, so a session that ends must take them
   * with it or the next person at a shared machine inherits them. One
   * `deleteDatabase`, no enumeration — the same shape as
   * `offline/cacheDb.ts`'s `destroy`, and for the same reason: nothing can be
   * missed when the whole file goes.
   *
   * `false`, never a throw, when the deletion did not happen — a database
   * another tab still holds open past the grace window is the ordinary case.
   * The caller reports that honestly rather than claiming a purge it did not
   * get. `true` always means an `onsuccess`, never an inference.
   */
  destroy(): Promise<boolean>;
}

export const OUTBOX_DB = 'ols-mail-outbox';
export const OUTBOX_STORE = 'messages';
const OUTBOX_DB_VERSION = 1;

/**
 * How long a `blocked` delete is given to complete before it is called a
 * failure. See `destroy` below for why `blocked` is not itself the answer.
 */
export const BLOCKED_GRACE_MS = 1_000;

/** The test double's extra handles, mirroring `MemoryOfflineCacheStorage`. */
export interface MemoryOutboxStorage extends OutboxStorage {
  /** True once `destroy()` has actually removed everything. */
  destroyed: boolean;
  /** Make the next `destroy()` report failure, as a blocked database would. */
  failNextDestroy(): void;
}

/** The test double, and nothing else uses it. */
export function createMemoryOutboxStorage(): MemoryOutboxStorage {
  const rows = new Map<string, OutboxMessage>();
  let failDestroy = false;
  const storage: MemoryOutboxStorage = {
    destroyed: false,
    failNextDestroy: () => {
      failDestroy = true;
    },
    destroy: () => {
      if (failDestroy) {
        failDestroy = false;
        return Promise.resolve(false);
      }
      rows.clear();
      storage.destroyed = true;
      return Promise.resolve(true);
    },
    all: () => Promise.resolve([...rows.values()]),
    get: (id) => Promise.resolve(rows.get(id)),
    add: (record) => {
      if (rows.has(record.id)) return Promise.reject(new Error('outbox: id already exists'));
      rows.set(record.id, record);
      return Promise.resolve();
    },
    mutate: (id, change) => {
      const current = rows.get(id);
      if (current === undefined) return Promise.resolve(undefined);
      const next = change(current);
      if (next === undefined) return Promise.resolve(undefined);
      rows.set(id, next);
      return Promise.resolve(next);
    },
    remove: (id) => {
      rows.delete(id);
      return Promise.resolve();
    },
    clear: () => {
      rows.clear();
      return Promise.resolve();
    },
  };
  return storage;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(OUTBOX_DB, OUTBOX_DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(OUTBOX_STORE)) {
        request.result.createObjectStore(OUTBOX_STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
    request.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

/** Resolves when the transaction is durable, rejects if it aborts. */
function settled(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

export function createIndexedDbOutboxStorage(): OutboxStorage {
  const withStore = async <T>(
    mode: IDBTransactionMode,
    body: (store: IDBObjectStore) => Promise<T>,
  ): Promise<T> => {
    const db = await openDatabase();
    try {
      const tx = db.transaction(OUTBOX_STORE, mode);
      const done = settled(tx);
      const result = await body(tx.objectStore(OUTBOX_STORE));
      if (mode === 'readwrite') await done;
      return result;
    } finally {
      db.close();
    }
  };

  return {
    all: () =>
      withStore('readonly', async (store) => {
        const rows = await promisify<OutboxMessage[]>(
          store.getAll() as IDBRequest<OutboxMessage[]>,
        );
        return rows;
      }),
    get: (id) =>
      withStore('readonly', async (store) => {
        const row = await promisify<OutboxMessage | undefined>(
          store.get(id) as IDBRequest<OutboxMessage | undefined>,
        );
        return row;
      }),
    add: (record) =>
      withStore('readwrite', async (store) => {
        await promisify(store.add(record));
      }),
    mutate: (id, change) =>
      withStore('readwrite', async (store) => {
        const current = await promisify<OutboxMessage | undefined>(
          store.get(id) as IDBRequest<OutboxMessage | undefined>,
        );
        if (current === undefined) return undefined;
        // Synchronous by contract: an `await` here would let the transaction
        // close under us and the write would throw TransactionInactiveError.
        const next = change(current);
        if (next === undefined) return undefined;
        await promisify(store.put(next));
        return next;
      }),
    remove: (id) =>
      withStore('readwrite', async (store) => {
        await promisify(store.delete(id));
      }),
    clear: () =>
      withStore('readwrite', async (store) => {
        await promisify(store.clear());
      }),
    destroy: () =>
      new Promise<boolean>((resolve) => {
        let settled = false;
        let grace: ReturnType<typeof setTimeout> | undefined;
        const finish = (ok: boolean): void => {
          if (settled) return;
          settled = true;
          if (grace !== undefined) clearTimeout(grace);
          resolve(ok);
        };
        let request: IDBOpenDBRequest;
        try {
          request = indexedDB.deleteDatabase(OUTBOX_DB);
        } catch {
          finish(false);
          return;
        }
        request.onsuccess = () => finish(true);
        request.onerror = () => finish(false);
        /*
         * `blocked` is not an answer. It is "still waiting".
         *
         * It fires while a connection is still open — another tab mid-flush,
         * or this page's own adapter with a transaction that has not quite
         * finished settling — and the request stays live, with `onsuccess`
         * following the moment the last connection closes. In practice that is
         * at once: WebKit fires `blocked` on essentially every sign-out here
         * and then completes the deletion inside ~100 ms (measured in a real
         * browser, which is the only place this code path runs at all).
         *
         * Reporting `false` the instant `blocked` arrived therefore warned, on
         * every WebKit sign-out, that unsent mail might still be on the disk —
         * about a database that was already gone. Crying wolf on the one
         * warning that has to be believed is its own kind of dishonesty. So
         * `blocked` starts a short grace instead, and only its expiry is a real
         * `false`. Nothing is weakened by this: `true` still requires an
         * `onsuccess` and is never assumed.
         */
        request.onblocked = () => {
          grace ??= setTimeout(() => finish(false), BLOCKED_GRACE_MS);
        };
      }),
  };
}

let factory: (() => OutboxStorage) | undefined;
let instance: OutboxStorage | undefined;

/** Swaps the implementation. Tests inject the in-memory one; nothing else calls it. */
export function setOutboxStorage(next: (() => OutboxStorage) | undefined): void {
  factory = next;
  instance = undefined;
}

export function outboxStorageSupported(): boolean {
  return factory !== undefined || typeof indexedDB !== 'undefined';
}

export function outboxStorage(): OutboxStorage {
  instance ??= (factory ?? createIndexedDbOutboxStorage)();
  return instance;
}
